import { NextPage, NextPageContext } from "next";
import { Post } from "../../types/Post";
import { notAuthorised } from "../../request";
import { CommonData } from "../../types/CommonData";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { getCommonData } from "../../helpers/common-data";
import { BlocksRenderer } from "../../components/BlocksRenderer";

import { getAnnouncements } from "../../helpers/getAnnouncements";
import { Announcement } from "../../types/Announcement";
import { PostCover } from "../../components/PostCover";
import { PostPagination } from "../../components/PostPagination";
import { Spacing } from "../../helpers/layout";
import { css } from "styled-components";
import { useResponsive } from "../../context/ResponsiveContext";
import { getFrontendUrl } from "../../helpers/utils";
import { SEOTags } from "../../components/SEOTags";
import Error from "../_error";
import { PostNavigation } from "../../components/PostNavigation";
import { getBlogPostRichSnippet } from "../../helpers/rich-snippets/blog-post";

export const PostDetail: NextPage<{
  data: Post | null;
  commonData: CommonData;
  announcements: Announcement[];
}> = (props) => {
  const { data } = props;
  const { isMobile, isTablet, isDesktop } = useResponsive();

  if (!data) {
    return (
      <Error
        commonData={props.commonData}
        announcements={props.announcements}
        statusCode={404}
      />
    );
  }

  const { blocks } = data;

  const showCover =
    (isMobile && data.show_mobile_header) ||
    (isTablet && data.show_tablet_header) ||
    (isDesktop && data.show_desktop_header);

  return (
    <>
      <SEOTags
        title={data.name}
        description={data.description}
        keywords={data.keywords}
        url={getFrontendUrl(`/blog/${data.slug}`)}
        featuredImage={data.featured_image}
        publishedTime={data.created}
        tags={data.tags}
        richSnippets={getBlogPostRichSnippet(data)}
      />
      <Header
        headerStyle={data.header_style}
        announcements={props.announcements}
      />
      {showCover && <PostCover post={data} />}
      <article
        css={css`
          padding: ${!isMobile && !isTablet ? `${Spacing.xxl} 0` : `0`};
        `}
      >
        <BlocksRenderer
          blocks={blocks}
          afterFirstBlock={!showCover && <PostNavigation post={data} />}
        />
      </article>
      <PostPagination post={data} />
      <Footer />
    </>
  );
};

interface Context extends NextPageContext {
  query: {
    slug: string;
  };
}

PostDetail.getInitialProps = async (ctx: Context) => {
  let data = null;

  try {
    const response = await notAuthorised.request<Post>({
      method: "GET",
      url: `/api/frontend/posts/${ctx.query.slug}/`,
    });

    data = response.data;
  } catch (error) {
    if (ctx.res) {
      ctx.res.statusCode = error.response.status;
    }
  }

  const commonData = await getCommonData(ctx.req);
  const announcements = await getAnnouncements("ALL");

  return {
    data: data,
    commonData: commonData,
    announcements,
  };
};

export default PostDetail;
